// src/components/seo/ExamLandingPage.tsx
// Professionele SEO & GEO geoptimaliseerde landingspagina voor WFT examens
// Modern Glassmorphism Design - Fully Responsive
// UPDATED: Gebruikt custom useSEO hook (geen externe dependencies)

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getExamBySlug } from '../../config/examConfigs';
import { useSEO } from '../../hooks/useSEO';
import AuthModal from '../AuthModal';

// Icons als SVG componenten voor performance
const CheckIcon = () => (
  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const QuestionIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface ExamLandingPageProps {
  examSlug?: string;
}

const ExamLandingPage: React.FC<ExamLandingPageProps> = ({ examSlug }) => {
  const params = useParams();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  
  // Get exam config from slug (prop or URL param)
  const slug = examSlug || params.examSlug || '';
  const exam = getExamBySlug(slug);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  // SEO Hook - moet ALTIJD aangeroepen worden (React hooks regels)
  useSEO(exam ? {
    title: exam.meta.title,
    description: exam.meta.description,
    canonicalUrl: `/${exam.slug}`,
    ogImage: exam.meta.ogImage,
    examType: exam.name,
    faqs: exam.faqs,
    courseInfo: {
      name: `${exam.fullName} Examentraining`,
      description: exam.longDescription,
      duration: `${exam.specs.duration} minuten`,
      numberOfQuestions: exam.specs.totalQuestions,
      passingScore: `${exam.specs.passingScore}%`,
      price: '0'
    }
  } : {
    title: 'Examen niet gevonden | WFT Study Buddy',
    description: 'Dit examen bestaat niet.',
    canonicalUrl: '/wft-examens'
  });
  
  if (!exam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Examen niet gevonden</h1>
          <Link to="/wft-examens" className="text-blue-600 hover:underline">
            Bekijk alle WFT examens →
          </Link>
        </div>
      </div>
    );
  }

 const handleStartFree = () => {
  setIsAuthModalOpen(true);
};

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
     {/* Auth Modal */}
<AuthModal 
  isOpen={isAuthModalOpen}
  onClose={() => setIsAuthModalOpen(false)}
  defaultTab="signup"
/>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl">📚</span>
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                WFT Study Buddy
              </span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/wft-examens" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Alle Examens
              </Link>
              <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-gray-900 transition font-medium">
                Features
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-gray-900 transition font-medium">
                FAQ
              </button>
              <button
                onClick={handleStartFree}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Start Gratis
              </button>
            </div>

            <button 
              onClick={handleStartFree}
              className="md:hidden bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Start Gratis
            </button>
          </div>
        </div>
      </nav>

    {/* Hero Section - Mobile Optimized */}
<section className="relative pt-20 pb-12 md:pt-24 lg:pt-32 md:pb-16 lg:pb-24 overflow-hidden">
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
    <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
  </div>

  <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
      <div className="text-center lg:text-left">
        
        {/* Breadcrumbs - HIDDEN on mobile */}
        <nav className="hidden md:flex items-center justify-center lg:justify-start space-x-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-gray-700">Home</Link>
          <span>/</span>
          <Link to="/wft-examens" className="hover:text-gray-700">WFT Examens</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{exam.name}</span>
        </nav>

        {/* CDFD Badge - HIDDEN on mobile */}
        <div className="hidden md:inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100 mb-6">
          <span className="text-lg">📋</span>
          <span className="text-sm font-semibold text-gray-700">Officiële CDFD Toetstermen</span>
        </div>

        {/* Title - Smaller on mobile */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-4 md:mb-6 speakable">
          {exam.fullName}{' '}
          <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Oefenvragen
          </span>
        </h1>

        {/* Description - SHORT on mobile, FULL on desktop */}
        <p className="md:hidden text-base text-gray-600 leading-relaxed mb-4">
          Bereid je optimaal voor met oefenvragen gebaseerd op officiële toetstermen.
        </p>
        <p className="hidden md:block text-lg lg:text-xl text-gray-600 leading-relaxed mb-6 speakable">
          {exam.longDescription}
        </p>

        {/* Exam Specs */}
        <div className="flex items-center justify-center lg:justify-start space-x-3 md:space-x-4 mb-4 md:mb-6">
          <div className="flex items-center space-x-1 text-gray-600 text-sm md:text-base">
            <QuestionIcon />
            <span>{exam.specs.totalQuestions} vragen</span>
          </div>
          <div className="flex items-center space-x-1 text-gray-600 text-sm md:text-base">
            <ClockIcon />
            <span>{exam.specs.duration} min</span>
          </div>
          <div className="flex items-center space-x-1 text-gray-600 text-sm md:text-base">
            <TargetIcon />
            <span>{exam.specs.passingScore}% slaaggrens</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-6">
          <button
            onClick={handleStartFree}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-bold text-base md:text-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
          >
            Start Gratis Oefenen
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <button
            onClick={() => document.getElementById('exam-info')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg hover:border-blue-400 hover:bg-white hover:shadow-lg transition-all duration-300"
          >
            Meer over dit examen
          </button>
        </div>

        {/* Pricing + Trust indicators */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2">
            <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Gratis: 5 vragen/dag</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Volledig proefexamen € 4,99</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span>Pro € 19,99/mnd</span>
            </div>
          </div>
          
          <div className="flex items-center justify-center lg:justify-start space-x-4 md:space-x-6 text-xs md:text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <CheckIcon />
              <span>Geen creditcard nodig</span>
            </div>
            <div className="flex items-center space-x-1">
              <CheckIcon />
              <span>Direct beginnen</span>
            </div>
          </div>
        </div>
      </div>

        

            {/* Right: Visual */}
           <div className="hidden lg:block relative">
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold">
                        {exam.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{exam.fullName}</div>
                        <div className="text-sm text-gray-500">Oefenexamen</div>
                      </div>
                    </div>
                    <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold">
                      Gratis
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-blue-600">Voorbeeldvraag</span>
                      <span className="text-sm text-gray-500">KB • 1 punt</span>
                    </div>
                    <p className="text-gray-800 font-medium mb-4">
                      {exam.id === 'schade-particulier' 
                        ? 'Welke verzekering dekt schade aan de inboedel door een gesprongen waterleiding?'
                        : exam.id === 'schade-zakelijk'
                        ? 'Welke verzekering dekt de aansprakelijkheid van een bedrijf voor schade aan derden?'
                        : `Wat is een kenmerk van ${exam.topics[0]?.toLowerCase() || 'dit onderwerp'}?`
                      }
                    </p>
                    <div className="space-y-2">
                      {['A', 'B', 'C', 'D'].map((letter, i) => (
                        <div 
                          key={letter}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            i === 0 
                              ? 'border-blue-300 bg-blue-50' 
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <span className="text-sm text-gray-600">{letter}.</span>
                          <span className="ml-2 text-gray-700">
                            {i === 0 ? 'Inboedelverzekering' : 'Antwoordoptie ' + letter}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{exam.specs.kbQuestions}</div>
                      <div className="text-xs text-gray-600">KB (Kennis & Begrip) vragen</div>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-indigo-600">{exam.specs.vcQuestions}</div>
                      <div className="text-xs text-gray-600">VC (Vaardigheden & Competenties) vragen</div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-purple-600">{exam.specs.pgQuestions}</div>
                      <div className="text-xs text-gray-600">PG (Professioneel Gedrag) vragen</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-400 to-orange-500 text-white px-4 py-2 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-lg font-bold">📋</div>
                    <div className="text-xs font-semibold">CDFD</div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-emerald-400 to-teal-500 text-white px-4 py-2 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-lg font-bold">⚡</div>
                    <div className="text-xs font-semibold">AI-Powered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Info Section */}
      <section id="exam-info" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 speakable">
              Alles over het {exam.fullName} Examen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Officiële exameninformatie op basis van CDFD-richtlijnen
            </p>
          </div>

     <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-white mb-4">
                <QuestionIcon />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{exam.specs.totalQuestions}</div>
              <div className="text-gray-600 font-medium">Examenvragen</div>
              <p className="text-sm text-gray-500 mt-2">
                {exam.specs.kbQuestions} KB (Kennis & Begrip) + {exam.specs.vcQuestions} VC (Vaardigheden & Competenties) + {exam.specs.pgQuestions} PG (Professioneel Gedrag)
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
              <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center text-white mb-4">
                <ClockIcon />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{exam.specs.duration} min</div>
              <div className="text-gray-600 font-medium">Examentijd</div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white mb-4">
                <TargetIcon />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{exam.specs.passingScore}%</div>
              <div className="text-gray-600 font-medium">Slaaggrens</div>
              <p className="text-sm text-gray-500 mt-2">
                Min. {exam.specs.passingPoints} van {exam.specs.maxPoints} punten
              </p>
            </div>
          </div>

   {/* Point distribution table - Mobile optimized */}
<div className="hidden md:block bg-gray-50 rounded-2xl p-4 md:p-8">
  <h3 className="text-xl font-bold text-gray-900 mb-6">Puntenverdeling {exam.fullName}</h3>
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm md:text-base">
      <thead>
        <tr className="border-b-2 border-gray-200">
          <th className="py-3 px-2 md:px-4 font-semibold text-gray-700">Onderdeel</th>
          <th className="py-3 px-2 md:px-4 font-semibold text-gray-700 text-center">Vragen</th>
          <th className="hidden md:table-cell py-3 px-4 font-semibold text-gray-700 text-center">Punten/vraag</th>
          <th className="py-3 px-2 md:px-4 font-semibold text-gray-700 text-center">Totaal</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        <tr>
          <td className="py-3 px-2 md:px-4">
            <span className="hidden md:inline">Kennis & Begrip (KB)</span>
            <span className="md:hidden">KB</span>
          </td>
          <td className="py-3 px-2 md:px-4 text-center">{exam.specs.kbQuestions}</td>
          <td className="hidden md:table-cell py-3 px-4 text-center">1</td>
          <td className="py-3 px-2 md:px-4 text-center font-semibold">{exam.specs.kbPoints}</td>
        </tr>
        <tr>
          <td className="py-3 px-2 md:px-4">
            <span className="hidden md:inline">Vaardigheden & Competenties (VC)</span>
            <span className="md:hidden">VC</span>
          </td>
          <td className="py-3 px-2 md:px-4 text-center">{exam.specs.vcQuestions}</td>
          <td className="hidden md:table-cell py-3 px-4 text-center">2</td>
          <td className="py-3 px-2 md:px-4 text-center font-semibold">{exam.specs.vcPoints}</td>
        </tr>
        <tr>
          <td className="py-3 px-2 md:px-4">
            <span className="hidden md:inline">Professioneel Gedrag (PG)</span>
            <span className="md:hidden">PG</span>
          </td>
          <td className="py-3 px-2 md:px-4 text-center">{exam.specs.pgQuestions}</td>
          <td className="hidden md:table-cell py-3 px-4 text-center">2</td>
          <td className="py-3 px-2 md:px-4 text-center font-semibold">{exam.specs.pgPoints}</td>
        </tr>
        <tr className="bg-gray-100 font-semibold">
          <td className="py-3 px-2 md:px-4">Totaal</td>
          <td className="py-3 px-2 md:px-4 text-center">{exam.specs.totalQuestions}</td>
          <td className="hidden md:table-cell py-3 px-4 text-center">-</td>
          <td className="py-3 px-2 md:px-4 text-center">{exam.specs.maxPoints}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Onderwerpen {exam.fullName}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {exam.topics.map((topic, index) => (
              <div 
                key={index}
                className="flex items-center space-x-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                  {index + 1}
                </div>
                <span className="text-gray-800 font-medium">{topic}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={handleStartFree}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Start met oefenen →
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-4">
              ✨ Waarom WFT Study Buddy
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              De slimste manier om je voor te bereiden
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Oefen met WFT-vragen op basis van officiële toetstermen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-3xl mb-6">🤖</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Gegenereerde Vragen</h3>
              <p className="text-gray-600">Oefenvragen gegenereerd door AI op basis van officiële CDFD toetstermen.</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-3xl mb-6">📊</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Slimme Voortgang</h3>
              <p className="text-gray-600">Zie precies waar je zwakke punten liggen en focus je studie.</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-3xl mb-6">📝</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Realistische Proefexamens</h3>
              <p className="text-gray-600">Oefen met proefexamens die de echte examenstructuur nabootsen.</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center text-3xl mb-6">📚</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Samenvattingen</h3>
              <p className="text-gray-600">Lees compacte samenvattingen per toetsterm.</p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-3xl mb-6">📱</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Overal Toegankelijk</h3>
              <p className="text-gray-600">Oefen op laptop, tablet of telefoon.</p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-2xl p-8 border border-rose-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center text-3xl mb-6">🎮</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Gamification</h3>
              <p className="text-gray-600">Verdien punten en badges terwijl je leert.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Veelgestelde vragen over {exam.fullName}
            </h2>
          </div>

          <div className="space-y-4">
            {exam.faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4 speakable">{faq.question}</h3>
                  <div className={`transform transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon />
                  </div>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed speakable">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Klaar om te beginnen met {exam.fullName}?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start vandaag nog gratis met oefenen!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleStartFree}
              className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Start Gratis Oefenen
            </button>
            <Link
              to="/wft-examens"
              className="bg-transparent text-white px-8 py-4 rounded-2xl font-semibold text-lg border-2 border-white/50 hover:bg-white/10 transition-all duration-300"
            >
              Bekijk alle examens
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">📚</span>
                <span className="font-bold text-xl text-white">WFT Study Buddy</span>
              </div>
              <p className="text-sm">De slimste manier om je voor te bereiden op je WFT examen.</p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">WFT Examens</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/wft-schade-particulier" className="hover:text-white transition">Schade Particulier</Link></li>
                <li><Link to="/wft-schade-zakelijk" className="hover:text-white transition">Schade Zakelijk</Link></li>
                <li><Link to="/wft-basis" className="hover:text-white transition">Basis</Link></li>
                <li><Link to="/wft-hypotheken" className="hover:text-white transition">Hypotheken</Link></li>
                <li><Link to="/wft-examens" className="hover:text-white transition">Alle examens →</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#features" className="hover:text-white transition">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition">Prijzen</Link></li>
                <li><Link to="/register" className="hover:text-white transition">Start Gratis</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
              <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm">
            <p>© <span suppressHydrationWarning>{new Date().getFullYear()}</span> WFT Study Buddy. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExamLandingPage;